
import Vue from 'vue';
import { mapState } from 'vuex';
import { auth } from '@/firebase';
import { ROUTER } from '@/router/constants';
import { PluginKind } from '@run-diffusion/shared';
import { extractPluginId, extractUserCode, isSignUpIntent, maskUserCodeInput, normalizeUserCode, submitConsent, SubmitConsentResult } from '@/utils/auth';

// The API base URL. Defaults to production for parity with the
// plugin's BASE_URL (D-6 in docs/refactors/photoshop-plugin-api-migration/decisions.md).
// Override with VUE_APP_API_BASE_URL in web/.env.local for local smoke
// against a locally-running Django (see restServer/LOCAL_SMOKE.md).
const API_BASE_URL =
	process.env.VUE_APP_API_BASE_URL || 'https://api2.rundiffusion.com/api/v2';

type State =
	| 'awaiting_login'
	| 'bad_code'
	| 'enter_code'
	| 'consent'
	| 'submitting'
	| 'done'
	| 'denied'
	| 'error';

export default Vue.extend({
	name: 'Auth',
	metaInfo: {
		title: 'Connect plugin | RunDiffusion',
	},
	data () {
		return {
			state: 'awaiting_login' as State,
			userCode: null as string | null,
			pluginId: null as PluginKind | null,
			// True when the plugin's "Create a free account" button opened
			// this page (?intent=signup). Routes an unauthenticated visitor
			// to the sign-up form instead of login. See ADR-0026.
			signUpIntent: false,
			errorMessage: '',
			manualCode: '',
			manualCodeError: '',
			ROUTER,
		};
	},
	computed: {
		...mapState(['authUser']),
		email (): string {
			return (this.authUser && this.authUser.email) || '';
		},
		enteredChars (): string[] {
			// De-dashed view of what the user has typed so far, for the
			// per-cell segmented display in the enter_code state.
			return (this.manualCode || '').replace('-', '').split('');
		},
		codeBlocks (): [string[], string[]] {
			// User code is minted as ``ABCD-1234`` (Crockford base32, 4+4).
			// Split on the dash into two character arrays so the template
			// can render two grouped glass pills side-by-side with a thin
			// separator — easier to verify against the plugin display
			// than 8 free-floating cells (anti-phishing per ADR-0001).
			const code = this.userCode || '';
			const parts = code.split('-');
			const a = (parts[0] || '').split('');
			const b = (parts[1] || '').split('');
			return [a, b];
		},
		cardClass (): Record<string, boolean> {
			return {
				'card--danger': this.state === 'bad_code' || this.state === 'error',
				'card--success': this.state === 'done',
			};
		},
	},
	created () {
		this.pluginId = extractPluginId(window.location.search);
		this.signUpIntent = isSignUpIntent(window.location.search);
		this.userCode = extractUserCode(window.location.search);
		if (!this.userCode) {
			// No (or malformed) code in the link — let the user type it
			// in rather than dead-ending on an error screen.
			this.state = 'enter_code';
			return;
		}
		this.evaluateState();
	},
	watch: {
		authUser () {
			// User signs in mid-page — re-evaluate.
			if (this.userCode && this.state === 'awaiting_login') {
				this.evaluateState();
			}
		},
	},
	methods: {
		evaluateState () {
			if (!this.authUser) {
				this.redirectToLogin();
				return;
			}
			if (!this.authUser.emailVerified) {
				// Plugin connection mints a long-lived session, so the account
				// must have a verified email first — same bar as the rest of
				// the app, just enforced in-component because /auth is a
				// login-adjacent page (see the emailVerifiedAuthRequired note
				// in router/index.ts). EmailVerification polls + sends the
				// email, then routes back to redirect=/auth?code=… once verified.
				this.redirectToEmailVerification();
				return;
			}
			this.state = 'consent';
		},
		onCodeInput (e: Event) {
			// Mask-as-you-type lives in the tested kernel (auth.ts):
			// uppercase, drop non-charset chars, cap at 8, auto-insert the
			// dash so the field reads XXXX-XXXX like the consent display.
			this.manualCode = maskUserCodeInput((e.target as HTMLInputElement).value);
			this.manualCodeError = '';
		},
		onSubmitCode () {
			const normalized = normalizeUserCode(this.manualCode);
			if (!normalized) {
				this.manualCodeError =
					'That doesn’t look right. Enter the 8-character code from your plugin, e.g. A7DC-3Q9K.';
				return;
			}
			this.manualCodeError = '';
			this.userCode = normalized;
			this.evaluateState();
		},
		redirectToLogin () {
			// Preserve the code through the Login round-trip so we land
			// back here with the original query.
			const redirect = this.authReturnPath();
			// A plugin sign-up intent (the Gate's "Create a free account"
			// button) drops the visitor into Login's sign-up state via
			// ?signup=true rather than the login form. Absent → login, the
			// default. See ADR-0026.
			const query: Record<string, string> = { redirect };
			if (this.signUpIntent) {
				query.signup = 'true';
			}
			this.$router.replace({
				name: ROUTER.LOGIN,
				query,
			}).catch(() => { /* swallow same-route navigation */ });
		},
		redirectToEmailVerification () {
			// Same code-preservation trick as redirectToLogin: EmailVerification
			// reads ?redirect and routes back to /auth?code=… once the address
			// is verified, dropping the user straight onto the consent screen.
			const redirect = this.authReturnPath();
			this.$router.replace({
				name: ROUTER.EMAIL_VERIFICATION,
				query: { redirect },
			}).catch(() => { /* swallow same-route navigation */ });
		},
		authReturnPath (): string {
			// Round-trip query for the Login / EmailVerification detour back
			// to /auth. Preserve both the connection code and the plugin id
			// so the post-connect onboarding still routes after the bounce.
			const params: string[] = [];
			if (this.userCode) {
				params.push(`code=${encodeURIComponent(this.userCode)}`);
			}
			if (this.pluginId) {
				params.push(`plugin=${encodeURIComponent(this.pluginId)}`);
			}
			return `/auth${params.length ? `?${params.join('&')}` : ''}`;
		},
		goToConnected () {
			// On a successful connect, hand off to the plugin-specific
			// onboarding walkthrough (it opens with its own "connected"
			// hero). Fall back to the generic done card when we don't know
			// which plugin connected (no ?plugin= on the verification URL).
			if (this.pluginId) {
				this.$router.replace({
					name: ROUTER.PLUGIN_ONBOARDING,
					params: { plugin: this.pluginId },
				}).catch(() => { /* swallow redundant navigation */ });
				return;
			}
			this.state = 'done';
		},
		onDeny () {
			this.state = 'denied';
		},
		async onAllow () {
			if (!this.authUser || !this.userCode) {
				this.state = 'error';
				this.errorMessage = 'Sign-in or code missing. Try again.';
				return;
			}
			this.state = 'submitting';
			try {
				const currentUser = auth.currentUser;
				if (!currentUser) {
					this.state = 'error';
					this.errorMessage = 'You appear to be signed out. Refresh and try again.';
					return;
				}
				const idToken = await currentUser.getIdToken();
				const refreshToken = currentUser.refreshToken;

				const result = await submitConsent({
					baseUrl: API_BASE_URL,
					userCode: this.userCode,
					idToken,
					refreshToken,
				});

				if (result.ok) {
					this.goToConnected();
					return;
				}
				this.state = 'error';
				this.errorMessage = this.mapResult(result);
				return;
			} catch (err) {
				this.state = 'error';
				this.errorMessage = 'Unexpected error. Please refresh and try again.';
			}
		},
		mapResult (result: SubmitConsentResult): string {
			if (result.ok) {
				return '';
			}
			// Pull ``reason`` off via a defensive cast because vue-cli-service's
			// TS compiler doesn't narrow the discriminated union inside Vue
			// methods. The runtime shape is guaranteed by auth.ts.
			const reason = (result as { reason: string }).reason;
			if (reason === 'code_expired_or_invalid') {
				return 'This connection code has expired or was already used. Re-run the "Sign in" step in your plugin.';
			}
			if (reason === 'network') {
				return 'Network error reaching the RunDiffusion API. Check your connection and try again.';
			}
			return 'Couldn’t connect your plugin. Please try again in a moment.';
		},
	},
});
